import { Config } from '@/utils/Config';
import { Consoler } from '@/utils/Consoler';
import { angularAlexMapDirectiveTemplate } from './angular-alex-map.directive.template';
import { EventBus } from '@/utils/EventBus';
import { InsectDamageReport } from '@/models/insectDamageReport';
import { PointStyleRule, PointStyleRuleColor, PointStyleRuleHelper } from '@/models/pointStyleRule';
import { Point } from '@/models/point';

export function angularAlexMapDirective(localizations, alConfig) {
    const directive = {
        restrict: 'E',
        template: angularAlexMapDirectiveTemplate.template,
        scope: { },

        link: (scope) => {
            scope.localizations = localizations;

            let points: Point[] = [];
            let pointStyleRules: PointStyleRule[] = [];

            scope.points = points;
            scope.pointStyles = [];

            scope.map = {
                isFullScreen: false,
                sidebarRight: false,

                pos: {
                    coordinates: [Config.DefaultMapCoordsLng, Config.DefaultMapCoordsLat], 
                    projection: Config.MapProjection,
                    zoom: 8
                },

                basePresets: [
                    { presetName: 'extweb/karta', displayName: localizations.mapLayers.extweb_karta }
                ],

                addonPresets: [
                    { presetName: 'extweb/sbo', displayName: localizations.mapLayers.extweb_sbo, visible: true },
                    { presetName: 'extweb/dsbo', displayName: localizations.mapLayers.extweb_dsbo, visible: false }
                ],

                activePresets: ['extweb/karta'],

                vectorLayers: []
            };

            scope.$root.$on('al-map:created', (event, alMap) => {
                scope.alMap = alMap;
                Consoler.debugLog('Alex map created', alMap);
            });

            scope.$root.$on('al-feature:selected', (event, alFeature, model) => {
                scope.$apply(() => {
                    setTimeout(() => {
                        Consoler.debugLog('Map feature selected', model);
                        EventBus.$emit(Config.MapPointSelectedEvent, model);
                    });
                });
            });
            scope.$root.$on('al-feature:deselected', () => {
                scope.$apply(() => {
                    Consoler.debugLog('Map feature deselected');
                    EventBus.$emit(Config.MapPointSelectedEvent, null);
                });
            });

            scope.$watch('map.pos.zoom', (zoom) => {
                setTimeout(() => {
                    if (zoom > Config.MapMaxZoom) {
                        Consoler.debugLog('Tried to zoom map above max', zoom,
                                            'setting to max', Config.MapMaxZoom);
                        scope.alMap.setZoom(Config.MapMaxZoom);
                    }
                });
            });

            EventBus.$on(Config.MapSetPointStyleRulesEvent, (rules: PointStyleRule[]) => {
                scope.$apply(() => {
                    Consoler.debugLog('Setting point styles to map', rules);

                    pointStyleRules = [];
                    rules.forEach(pointStyleRule => {

                        // Create one rule for newGeneration..
                        pointStyleRules.push(
                            PointStyleRuleHelper.clone(pointStyleRule, true)
                        );

                        // .. and one rule without newGeneration
                        pointStyleRules.push(
                            PointStyleRuleHelper.clone(pointStyleRule, false)
                        );
                    });

                    // Add point styles for each rule created
                    scope.pointStyles = [];
                    pointStyleRules.forEach(pointStyleRule => {
                        scope.pointStyles.push({
                            circle: getPointStyleCircle(pointStyleRule)
                        });
                    });
                });
            });

            EventBus.$on(Config.MapSetReportPointsEvent, (reports: InsectDamageReport[]) => {
                scope.$apply(() => {
                    Consoler.debugLog('Setting report points to map', reports);
                    points = [];
                    scope.points = points;

                    reports.forEach(report => {
                        let styleIndex = -1;
                        for (let i = 0; i < pointStyleRules.length; i++) {
                            if (checkPointStyleRuleAgainstReport(
                                    report,
                                    pointStyleRules[i])) {
                                styleIndex = i;
                                break;
                            }
                        }

                        points.push(
                        {
                            coordinates: [report.longitude, report.latitude],
                            projection: Config.MapProjection,
                            name: report.trapGroupName,
                            quantity: report.quantity,
                            styleIndex,
                            trapGroupId: report.trapGroupId,
                            newGenerationSinceWeek: report.newGenerationSinceWeek
                        });
                    });
                });
            });

            EventBus.$on(Config.MapClearSelectedPointsEvent, () => {
                scope.$apply(() => {
                    setTimeout(() => {
                        scope.alMap.clearSelections();
                    });
                });
            });

            scope.fullscreen = (isFullScreen: boolean) => {
                scope.map.isFullScreen = isFullScreen;
                EventBus.$emit(Config.MapSetFullScreenEvent, isFullScreen);
                scrollToTop()
                setTimeout(() => {
                    scope.alMap.updateSize();
                });
            };

            scope.sidebarRight = (show: boolean) => {
                EventBus.$emit(Config.MapSetShowSidebarRightEvent, show);
            };

            function getPointStyleCircle(pointStyleRule: PointStyleRule): any {
                let fillColor = [255, 255, 255, 1];
                if (pointStyleRule.color === PointStyleRuleColor.Green) {
                    // from unity variable $sodraGreen: #00854a
                    fillColor = [0, 133, 74, 1];
                }
                else if (pointStyleRule.color === PointStyleRuleColor.Yellow) {
                    // from unity variable $yellow: #ffeb46
                    fillColor = [255, 235, 70, 1];
                }
                else if (pointStyleRule.color === PointStyleRuleColor.Red) {
                    // from unity variable $red: #e4624a
                    fillColor = [228, 98, 74, 1];
                }

                return {
                    stroke: {
                        color: pointStyleRule.newGeneration ? [0, 0, 0, 1] : [255, 255, 255, 1],
                        width: pointStyleRule.newGeneration ? 5 : 1
                    },
                    fill: {
                        color: fillColor
                    },
                    radius: 18
                };
            }

            function checkPointStyleRuleAgainstReport(report: InsectDamageReport, rule: PointStyleRule) {
                return (rule.quantityFrom === null ||
                            (rule.quantityFrom !== null && rule.quantityFrom <= report.quantity)) &&
                       (rule.quantityTo === null || (rule.quantityTo !== null && rule.quantityTo >= report.quantity)) &&
                       ((rule.newGeneration && report.newGenerationSinceWeek !== null) ||
                        (!rule.newGeneration && report.newGenerationSinceWeek === null));
            }

            function scrollToTop(): void {
                window.scrollTo(0, 0);
            }
        }
    };
    return directive;
}